// ============================================================================
// NOTIFICATIONDROPDOWN.JS — Campana + panel lateral (slide-over) de notificaciones
// ----------------------------------------------------------------------------
// El botón campana abre un panel deslizante desde la derecha con:
//   - Dos pestañas: "Todos" (menciones + comentarios) y "Menciones".
//   - Por fila: avatar real del actor, contenido, tiempo, estado no-leído, y al
//     pasar el ratón dos acciones: marcar leída (✓) y borrar (🗑).
//   - Clic en el cuerpo → navega a la tarea y marca leída.
//   - Cabecera: marcar todas leídas + borrar todas + cerrar.
// Menciones = filas reales en `notificaciones` (borrado real). Comentarios =
// estado derivado "sin leer" → borrar equivale a marcar leído (desaparecen).
// Badge de la campana = no leídas (comentarios + menciones).
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Clock, Check, Trash2, X, AtSign, MessageSquare, CheckCheck
} from 'lucide-react';
import { useTaskStore } from '../store/TaskStore';
import { useAuth } from '../auth/AuthContext';
import MentionService from '../services/MentionService';
import apiClient from '../services/apiClient';
import Avatar from './Avatar';
import '../styles/notification-dropdown.css';

import { parseServerDate } from '../utils/serverDate';
function NotificationDropdown() {
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('todos'); // 'todos' | 'menciones'
  const [commentNotifications, setCommentNotifications] = useState([]);
  const [mentionNotifications, setMentionNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [commentUnread, setCommentUnread] = useState(0);
  const [mentionUnread, setMentionUnread] = useState(0);

  const tasks = useTaskStore((s) => s.tasks);
  const { user, token } = useAuth();

  const totalUnread = commentUnread + mentionUnread;

  // Refs: campana (restaurar foco), botón cerrar (mover foco al abrir), secuencia
  // de carga (ignora respuestas obsoletas) y acciones en vuelo (anti doble-clic).
  const bellRef = useRef(null);
  const closeBtnRef = useRef(null);
  const openedRef = useRef(false);
  const loadSeq = useRef(0);
  const actingRef = useRef(new Set());

  // ==========================================================================
  // CARGA
  // ==========================================================================
  const loadNotifications = async () => {
    if (!user?.id || !token) return;
    const seq = ++loadSeq.current; // solo la carga más reciente aplica su resultado
    setIsLoading(true);
    try {
      const summaryPromise = apiClient.get('notification_summary', { user_id: user.id })
        .catch(() => null);
      const tasksPromise = apiClient.get('tasks_with_new_comments', { user_id: user.id, limit: 20 })
        .catch(() => null);
      const mentionsPromise = MentionService.getMyNotifications({ limit: 30 });

      const [summaryData, tasksData, mentionsResult] = await Promise.all([summaryPromise, tasksPromise, mentionsPromise]);
      // Respuesta obsoleta (llegó otra carga después): no la apliques.
      if (seq !== loadSeq.current) return;
      const summaryOk = summaryData?.status === 'success';

      setCommentNotifications(tasksData?.status === 'success' && Array.isArray(tasksData.data) ? tasksData.data : []);
      setCommentUnread(summaryOk ? (summaryData.data?.total_unread || 0) : 0);

      const mentions = Array.isArray(mentionsResult?.notifications) ? mentionsResult.notifications : [];
      setMentionNotifications(mentions);
      if (summaryOk && typeof summaryData.data?.unread_mentions === 'number') {
        setMentionUnread(summaryData.data.unread_mentions);
      } else {
        setMentionUnread(typeof mentionsResult?.unreadCount === 'number'
          ? mentionsResult.unreadCount
          : mentions.filter(m => !m.leido).length);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
  };

  // ==========================================================================
  // ACCIONES — MENCIONES (fila real)
  // ==========================================================================
  const markMentionRead = (id) => {
    const m = mentionNotifications.find(x => x.id === id);
    if (!m || m.leido) return;
    const key = `mr-${id}`;
    if (actingRef.current.has(key)) return; // anti doble-clic sincrónico
    actingRef.current.add(key);
    setMentionNotifications(prev => prev.map(x => (x.id === id ? { ...x, leido: 1 } : x)));
    setMentionUnread(prev => Math.max(0, prev - 1));
    MentionService.markRead(id)
      .then(res => { if (!res?.success) throw new Error(); })
      .catch(() => {
        setMentionNotifications(prev => prev.map(x => (x.id === id ? { ...x, leido: 0 } : x)));
        setMentionUnread(prev => prev + 1);
      })
      .finally(() => actingRef.current.delete(key));
  };

  const deleteMention = (id) => {
    const key = `md-${id}`;
    if (actingRef.current.has(key)) return;
    const idx = mentionNotifications.findIndex(x => x.id === id);
    if (idx === -1) return;
    actingRef.current.add(key);
    const m = mentionNotifications[idx];
    setMentionNotifications(prev => prev.filter(x => x.id !== id));
    if (!m.leido) setMentionUnread(prev => Math.max(0, prev - 1));
    MentionService.deleteNotification(id)
      .then(res => { if (!res?.success) throw new Error(); })
      .catch(() => {
        setMentionNotifications(prev => {
          const copy = [...prev];
          copy.splice(Math.min(idx, copy.length), 0, m);
          return copy;
        });
        if (!m.leido) setMentionUnread(prev => prev + 1);
      })
      .finally(() => actingRef.current.delete(key));
  };

  const handleMentionClick = (m) => {
    if (!m) return;
    markMentionRead(m.id);
    close();
    navigate(m.url || (m.tarea_ref ? `/task/detail/${m.tarea_ref}` : '/task/'));
  };

  // ==========================================================================
  // ACCIONES — COMENTARIOS (estado derivado; borrar == marcar leído)
  // ==========================================================================
  const markCommentRead = async (taskId) => {
    if (!taskId) return;
    const key = `c-${taskId}`;
    if (actingRef.current.has(key)) return;
    const idx = commentNotifications.findIndex(x => x?.taskId === taskId);
    const c = idx >= 0 ? commentNotifications[idx] : null;
    // Una fila = una tarea con N comentarios sin leer; el badge cuenta comentarios
    // individuales → decrementar por N, no por 1.
    const dec = c?.newCommentsCount || 1;
    actingRef.current.add(key);
    setCommentNotifications(prev => prev.filter(x => x?.taskId !== taskId));
    setCommentUnread(prev => Math.max(0, prev - dec));
    try {
      const res = await apiClient.post('mark_comments_read', { task_id: taskId, user_id: user.id });
      if (res?.status !== 'success') throw new Error();
    } catch {
      if (c) setCommentNotifications(prev => {
        const copy = [...prev];
        copy.splice(Math.min(idx, copy.length), 0, c);
        return copy;
      });
      setCommentUnread(prev => prev + dec);
    } finally {
      actingRef.current.delete(key);
    }
  };

  const handleCommentClick = (taskId) => {
    if (!taskId) return;
    markCommentRead(taskId);
    close();
    navigate(`/task/detail/${taskId}`);
  };

  // ==========================================================================
  // ACCIONES GLOBALES
  // ==========================================================================
  const handleMarkAllRead = async () => {
    const prevM = mentionNotifications, prevMU = mentionUnread;
    setMentionNotifications(prev => prev.map(m => ({ ...m, leido: 1 })));
    setMentionUnread(0);
    const mp = MentionService.markAllRead()
      .then(res => { if (!res?.success) throw new Error(); })
      .catch(() => { setMentionNotifications(prevM); setMentionUnread(prevMU); });

    const cr = await apiClient.post('mark_all_notifications_read', { user_id: user.id }).catch(() => null);
    if (cr?.status === 'success') { setCommentNotifications([]); setCommentUnread(0); }
    await mp;
  };

  const handleDeleteAll = async () => {
    const prevM = mentionNotifications, prevC = commentNotifications, prevMU = mentionUnread, prevCU = commentUnread;
    setMentionNotifications([]); setCommentNotifications([]); setMentionUnread(0); setCommentUnread(0);

    const [mRes, cRes] = await Promise.all([
      MentionService.deleteAllNotifications(),
      apiClient.post('mark_all_notifications_read', { user_id: user.id }).catch(() => null)
    ]);
    if (!mRes?.success) { setMentionNotifications(prevM); setMentionUnread(prevMU); }
    if (cRes?.status !== 'success') { setCommentNotifications(prevC); setCommentUnread(prevCU); }
  };

  // ==========================================================================
  // APERTURA / CIERRE
  // ==========================================================================
  const open = () => { setIsOpen(true); loadNotifications(); };
  const close = () => setIsOpen(false);
  const toggle = () => (isOpen ? close() : open());

  // Cargar para el badge cuando cambian tareas/usuario, y refrescar al CERRAR el
  // panel. NO recargar mientras está ABIERTO: evita pisar un cambio optimista
  // (borrar/marcar) que el usuario acaba de hacer.
  // Perf: DEBOUNCED (1,5s) — cada cambio de `tasks` disparaba 3 endpoints de
  // notificaciones; ahora las ráfagas (arranque, mutaciones seguidas) colapsan
  // en una única recarga.
  useEffect(() => {
    if (!user?.id || !token || isOpen) return;
    const t = setTimeout(loadNotifications, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, user?.id, token, isOpen]);

  // Foco: al abrir, llevarlo al botón cerrar del panel; al cerrar, devolverlo a la
  // campana (solo si el panel había llegado a abrirse; no en el montaje inicial).
  useEffect(() => {
    if (isOpen) {
      openedRef.current = true;
      const t = setTimeout(() => closeBtnRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    if (openedRef.current) {
      openedRef.current = false;
      bellRef.current?.focus();
    }
  }, [isOpen]);

  // Esc para cerrar + bloqueo de scroll del body mientras está abierto.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // ==========================================================================
  // HELPERS
  // ==========================================================================
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseServerDate(dateString);
      const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
      if (diffMin < 1) return 'Ahora';
      if (diffMin < 60) return `${diffMin}m`;
      if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h`;
      return `${Math.floor(diffMin / 1440)}d`;
    } catch { return ''; }
  };

  const unifiedItems = (() => {
    const items = [];
    (Array.isArray(mentionNotifications) ? mentionNotifications : []).forEach(m =>
      items.push({ kind: 'mention', key: `m-${m.id}`, date: m.fecha_creacion, data: m }));
    (Array.isArray(commentNotifications) ? commentNotifications : []).forEach((c, i) =>
      items.push({ kind: 'comment', key: `c-${c?.taskId || i}`, date: c?.latestCommentDate, data: c }));
    items.sort((a, b) => {
      const ta = a.date ? new Date(String(a.date).replace(' ', 'T')).getTime() : 0;
      const tb = b.date ? new Date(String(b.date).replace(' ', 'T')).getTime() : 0;
      return tb - ta;
    });
    return items;
  })();

  const mentionCount = mentionNotifications.length;
  const todosCount = unifiedItems.length;
  const visibleItems = activeTab === 'menciones'
    ? unifiedItems.filter(it => it.kind === 'mention')
    : unifiedItems;

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div className="notification-dropdown">
      <button
        ref={bellRef}
        onClick={toggle}
        className="notification-button"
        title={totalUnread > 0 ? `${totalUnread} notificaciones nuevas` : 'Notificaciones'}
        aria-label="Notificaciones"
        aria-expanded={isOpen}
      >
        <Bell size={16} />
        {totalUnread > 0 && (
          <span className="notification-count">{totalUnread > 99 ? '99+' : totalUnread}</span>
        )}
      </button>

      {createPortal(
        <div
          className={`notif-overlay${isOpen ? ' is-open' : ''}`}
          onClick={close}
          aria-hidden={!isOpen}
        >
          <aside
            className="notif-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Notificaciones"
          >
            <header className="notif-panel__head">
              <div className="notif-panel__title">
                <Bell size={18} />
                <span>Notificaciones</span>
                {totalUnread > 0 && <span className="notif-panel__badge">{totalUnread}</span>}
              </div>
              <div className="notif-panel__head-actions">
                {totalUnread > 0 && (
                  <button className="notif-head-btn" onClick={handleMarkAllRead} title="Marcar todas como leídas">
                    <CheckCheck size={15} />
                    <span>Marcar leídas</span>
                  </button>
                )}
                {todosCount > 0 && (
                  <button className="notif-head-btn notif-head-btn--danger" onClick={handleDeleteAll} title="Borrar todas">
                    <Trash2 size={15} />
                    <span>Borrar todas</span>
                  </button>
                )}
                <button ref={closeBtnRef} className="notif-panel__close" onClick={close} aria-label="Cerrar">
                  <X size={18} />
                </button>
              </div>
            </header>

            <div className="notif-tabs" role="tablist">
              <button
                role="tab"
                aria-selected={activeTab === 'todos'}
                className={`notif-tab${activeTab === 'todos' ? ' is-active' : ''}`}
                onClick={() => setActiveTab('todos')}
              >
                Todos {todosCount > 0 && <span className="notif-tab__count">{todosCount}</span>}
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'menciones'}
                className={`notif-tab${activeTab === 'menciones' ? ' is-active' : ''}`}
                onClick={() => setActiveTab('menciones')}
              >
                Menciones {mentionCount > 0 && <span className="notif-tab__count">{mentionCount}</span>}
              </button>
            </div>

            <div className="notif-list">
              {isLoading && visibleItems.length === 0 ? (
                <div className="notif-state">
                  <div className="notif-spinner" />
                  <span>Cargando…</span>
                </div>
              ) : visibleItems.length === 0 ? (
                <div className="notif-state notif-empty">
                  <div className="notif-empty__ic"><Bell size={22} /></div>
                  <h4>Estás al día</h4>
                  <p>{activeTab === 'menciones' ? 'No tienes menciones' : 'No hay notificaciones'}</p>
                </div>
              ) : (
                visibleItems.map(it => it.kind === 'mention'
                  ? renderMention(it.data, it.key)
                  : renderComment(it.data, it.key))
              )}
            </div>
          </aside>
        </div>,
        document.body
      )}
    </div>
  );

  // --- Fila de MENCIÓN ---
  function renderMention(m, itemKey) {
    const unread = !m.leido;
    return (
      <div
        key={itemKey || `m-${m.id}`}
        className={`notif-item${unread ? ' is-unread' : ''}`}
        onClick={() => handleMentionClick(m)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') handleMentionClick(m); }}
      >
        <div className="notif-item__avatar">
          <Avatar name={m.actor_nombre || 'Alguien'} src={m.actor_avatar} size={38} title="" />
          <span className="notif-item__type notif-item__type--mention" aria-hidden="true"><AtSign size={11} /></span>
        </div>

        <div className="notif-item__body">
          <div className="notif-item__title">
            <strong>{m.actor_nombre || 'Alguien'}</strong> te mencionó
          </div>
          <div className="notif-item__ref">
            {m.tarea_ref && <span className="notif-ref-chip">{m.tarea_ref}</span>}
            {m.tarea_nombre && <span className="notif-ref-task">· {m.tarea_nombre}</span>}
          </div>
          {m.mensaje && <div className="notif-item__excerpt">{m.mensaje}</div>}
          <div className="notif-item__meta"><Clock size={12} />{formatTimeAgo(m.fecha_creacion)}</div>
        </div>

        <div className="notif-item__actions">
          {unread && (
            <button className="notif-act" title="Marcar leída"
              onClick={(e) => { e.stopPropagation(); markMentionRead(m.id); }}>
              <Check size={15} />
            </button>
          )}
          <button className="notif-act notif-act--danger" title="Borrar"
            onClick={(e) => { e.stopPropagation(); deleteMention(m.id); }}>
            <Trash2 size={15} />
          </button>
        </div>

        {unread && <span className="notif-item__dot" aria-hidden="true" />}
      </div>
    );
  }

  // --- Fila de COMENTARIO ---
  function renderComment(c, itemKey) {
    return (
      <div
        key={itemKey || `c-${c?.taskId}`}
        className="notif-item is-unread"
        onClick={() => handleCommentClick(c?.taskId)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') handleCommentClick(c?.taskId); }}
      >
        <div className="notif-item__avatar">
          <Avatar name={c?.lastCommenter || 'Usuario'} src={c?.lastCommenterAvatar} size={38} title="" />
          <span className="notif-item__type notif-item__type--comment" aria-hidden="true"><MessageSquare size={11} /></span>
        </div>

        <div className="notif-item__body">
          <div className="notif-item__title"><strong>{c?.taskName || 'Tarea'}</strong></div>
          <div className="notif-item__ref">
            <span className="notif-ref-task">
              {c?.newCommentsCount || 1} comentario{(c?.newCommentsCount || 1) > 1 ? 's' : ''} · por {c?.lastCommenter || 'Usuario'}
            </span>
          </div>
          {c?.lastCommentPreview && <div className="notif-item__excerpt">{c.lastCommentPreview}</div>}
          <div className="notif-item__meta"><Clock size={12} />{formatTimeAgo(c?.latestCommentDate)}</div>
        </div>

        <div className="notif-item__actions">
          <button className="notif-act" title="Marcar leído"
            onClick={(e) => { e.stopPropagation(); markCommentRead(c?.taskId); }}>
            <Check size={15} />
          </button>
          <button className="notif-act notif-act--danger" title="Descartar"
            onClick={(e) => { e.stopPropagation(); markCommentRead(c?.taskId); }}>
            <Trash2 size={15} />
          </button>
        </div>

        <span className="notif-item__dot" aria-hidden="true" />
      </div>
    );
  }
}

export default NotificationDropdown;
